// React
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

// Actions
import { listDays, deleteDayAction } from "../../actions/dayActions";

// Layout
import PageContainer from "../../components/layouts/Containers/PageContainer";
import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";
import { LoadingTile } from "../../components/ui/Loading/LoadingTile";

// Utils
import { dateFormatter } from "../../utils/UtilityFunctions";
import {
  hasScore,
  isDayComplete,
  needsFeelingScore,
  sortDaysByLogDate,
} from "../../utils/dayProgress";
import useResponsiveState from "../../hooks/useResponsiveState";
import { useLoadingContent } from "../../hooks/useLoadingContent";
import { usePersistentLoading } from "../../hooks/usePersistentLoading";
import { useLoadingFloor } from "../../hooks/useLoadingFloor";

// Sub-components
import { DayListPanel } from "./ProfileLogComponents/DayListPanel";
import { DayPreviewPanel } from "./ProfileLogComponents/DayPreviewPanel";

// Styling
import "./profile_menu.css";
import SEO from "../../components/seo/SEO";

export const ProfileLogScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { days } = useSelector((state) => state.dayList);
  const { viewType } = useResponsiveState();
  const loadingContent = useLoadingContent();

  const pageLoading = useLoadingFloor(8000, loadingContent);
  const { isShowing, tileProps } = usePersistentLoading(
    pageLoading,
    loadingContent
  );
  const [search, setSearch] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterWeekday, setFilterWeekday] = useState("");
  /* Lazy initialiser only. The backlog tile links here with the filter
     pre-applied; reading the param on every render would fight the user the
     moment they cleared it. */
  const [filterStatus, setFilterStatus] = useState(() => {
    const status = searchParams.get("status");
    return status === "needs-score" || status === "complete" ? status : "";
  });

  /* Changing the filter drops the param, so a reload cannot resurrect the
     filter the user just cleared. */
  const handleFilterStatus = (value) => {
    setFilterStatus(value);
    if (searchParams.has("status")) {
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      setSearchParams(next, { replace: true });
    }
  };
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const selectedCardRef = useRef(null);
  const selectedIndexRef = useRef(0);
  const viewTypeRef = useRef(viewType);

  useEffect(() => { selectedIndexRef.current = selectedIndex; }, [selectedIndex]);
  useEffect(() => { viewTypeRef.current = viewType; }, [viewType]);

  const deleteHandler = async (id) => {
    try {
      await dispatch(deleteDayAction(id));
      dispatch(listDays());
    } catch (error) {
      alert(`Failed to delete day: ${error.message}`);
    }
  };

  const handleNav = (id) => navigate(`/today/${id}`);

  const filterOptions = useMemo(() => {
    if (!days) return { years: [], months: [], weekdays: [] };
    const yearSet = new Set();
    const monthSet = new Set();
    const weekdaySet = new Set();
    days.forEach((d) => {
      if (!d.logDate) return;
      const date = new Date(d.logDate.slice(0, 10) + "T12:00:00");
      yearSet.add(date.getFullYear());
      monthSet.add(date.getMonth());
      weekdaySet.add(date.getDay());
    });
    return {
      years: [...yearSet].sort(),
      months: [...monthSet].sort((a, b) => a - b),
      weekdays: [...weekdaySet].sort((a, b) => a - b),
    };
  }, [days]);

  const filteredDays = useMemo(() => {
    if (!days || days.length === 0) return [];
    let result = days;
    if (search) {
      result = result.filter((val) =>
        val.logDate &&
        dateFormatter(val.logDate).toLowerCase().includes(search.toLowerCase())
      );
    }
    // Nothing guarantees a logDate — a day written into before the date was
    // picked has none — so every date filter has to tolerate its absence
    // rather than throwing on .slice().
    const dateOf = (d) =>
      d.logDate ? new Date(d.logDate.slice(0, 10) + "T12:00:00") : null;
    if (filterYear) {
      result = result.filter(
        (d) => dateOf(d)?.getFullYear().toString() === filterYear
      );
    }
    if (filterMonth) {
      result = result.filter(
        (d) => dateOf(d)?.getMonth().toString() === filterMonth
      );
    }
    if (filterWeekday) {
      result = result.filter(
        (d) => dateOf(d)?.getDay().toString() === filterWeekday
      );
    }
    if (filterStatus === "needs-score") {
      result = result.filter(needsFeelingScore);
    } else if (filterStatus === "complete") {
      result = result.filter(isDayComplete);
    }
    // Newest first. This screen exists to find a day, and Mongo's insertion
    // order means nothing to the reader.
    return sortDaysByLogDate(result, "desc");
  }, [days, search, filterYear, filterMonth, filterWeekday, filterStatus]);

  const scoreOpacity = useMemo(() => {
    const allScores = filteredDays
      .flatMap((d) => [
        hasScore(d.startScore) ? Number(d.startScore) : null,
        hasScore(d.endScore) ? Number(d.endScore) : null,
      ])
      .filter((s) => s != null && !isNaN(s));

    if (allScores.length === 0) return () => 1;

    const min = Math.min(...allScores);
    const max = Math.max(...allScores);
    const range = max - min;

    if (range === 0) return () => 0.75;

    const lowThreshold = min + range / 3;
    const highThreshold = min + (2 * range) / 3;

    return (score) => {
      if (!hasScore(score)) return 1;
      const n = Number(score);
      if (n <= lowThreshold) return 0.5;
      if (n >= highThreshold) return 1;
      return 0.75;
    };
  }, [filteredDays]);

  // The list badges use the same terciles as scoreOpacity, but as classes
  // rather than an inline rgba(): a component stylesheet must not carry a raw
  // colour, and the inline one used 0,74,35 where the forest ramp is 0,74,34.
  // scoreOpacity stays — DayPreviewPanel uses it with a cream ramp on dark.
  const scoreBucket = useMemo(() => {
    const opacityToBucket = { 0.5: "low", 0.75: "mid", 1: "high" };
    return (score) => opacityToBucket[scoreOpacity(score)] ?? "high";
  }, [scoreOpacity]);

  // Clamp selectedIndex when filteredDays shrinks
  useEffect(() => {
    if (filteredDays.length > 0) {
      setSelectedIndex((i) => Math.min(i, filteredDays.length - 1));
    }
  }, [filteredDays.length]);

  // Scroll the selected card into view
  useEffect(() => {
    selectedCardRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (viewTypeRef.current === "mobile") return;
      if (document.activeElement.tagName === "INPUT") return;
      if (document.activeElement.tagName === "SELECT") return;
      if (!filteredDays.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filteredDays.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Escape") {
        setCompareMode(false);
        setCompareIds([]);
      } else if (e.key === "Enter") {
        const day = filteredDays[selectedIndexRef.current];
        if (day) navigate(`/today/${day.id}`);
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [filteredDays, navigate]);

  const selectedDay = filteredDays[selectedIndex] ?? null;
  const compareDays = compareIds
    .map((id) => filteredDays.find((d) => d.id === id))
    .filter(Boolean);

  const handleSelect = (index) => {
    setSelectedIndex(index);
    if (viewType === "mobile") setPreviewOpen(true);
  };

  const handleToggleCompareMode = () => {
    if (!compareMode) {
      setCompareMode(true);
      if (selectedDay) setCompareIds([selectedDay.id]);
    } else {
      setCompareMode(false);
      setCompareIds([]);
    }
  };

  const handleToggleCard = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) return prev;
      return [...prev, id];
    });
  };

  const noContent = !days || days.length === 0;

  return (
    <>
      <SEO
        title="Journal Log"
        description="View a log of all of your journal days to view, delete or update your entries"
        canonicalUrl={"https://nowasteddays.io/my-account/journal"}
      />
      <Header variant="light" />
      <PageContainer className="profile__menu--screen">
        {isShowing ? (
          <LoadingTile content={loadingContent} {...tileProps} />
        ) : noContent ? (
          <div className="tooltip__container">
            <h5 className="sickTooltip">
              When you log days in your journal, they will appear here.
            </h5>
          </div>
        ) : (
          <div className="log__layout">
            <DayListPanel
              days={filteredDays}
              selectedIndex={selectedIndex}
              onSelect={handleSelect}
              compareMode={compareMode}
              compareIds={compareIds}
              onToggleCard={handleToggleCard}
              onClearCompare={() => setCompareIds([])}
              onToggleCompareMode={handleToggleCompareMode}
              search={search}
              onSearchChange={(e) => setSearch(e.target.value)}
              selectedCardRef={selectedCardRef}
              filterYear={filterYear}
              filterMonth={filterMonth}
              filterWeekday={filterWeekday}
              filterStatus={filterStatus}
              onFilterYear={setFilterYear}
              onFilterMonth={setFilterMonth}
              onFilterWeekday={setFilterWeekday}
              onFilterStatus={handleFilterStatus}
              filterOptions={filterOptions}
              scoreBucket={scoreBucket}
              viewType={viewType}
              onVisit={handleNav}
              onDelete={deleteHandler}
            />
            <DayPreviewPanel
              day={selectedDay}
              compareDays={compareDays}
              compareMode={compareMode}
              onVisit={handleNav}
              onDelete={deleteHandler}
              onBack={() => setPreviewOpen(false)}
              isOpen={previewOpen}
              viewType={viewType}
              scoreOpacity={scoreOpacity}
            />
          </div>
        )}
      </PageContainer>
      <TestFooter />
    </>
  );
};
